/**
 * Kinetic energy, translational and rotational.
 *
 *   KE = ½ m v²        (a mass m moving at speed v)
 *   KE = ½ I ω²        (a body of moment of inertia I spinning at angular speed ω)
 *
 * OpenStax University Physics Vol. 1, §7.2 (translational) and §10.4 (rotational). These
 * are the non-relativistic forms: they are within 1% of the relativistic value up to about 11.5%
 * of the speed of light (the correction is roughly ¾ (v/c)²).
 */

import type {
  RelationDef,
  RelationError,
} from "@/lib/tool-kit/physics-mechanics/relation";
import {
  ANGULAR_SPEED,
  ENERGY,
  MASS,
  MOMENT_OF_INERTIA,
  SPEED,
} from "@/lib/tool-kit/physics-mechanics/units";

const fail = (error: string): RelationError => ({ error });

export type KineticKey = "E" | "m" | "v";

export const KINETIC: RelationDef<KineticKey> = {
  keys: ["E", "m", "v"],
  symbols: { E: "KE", m: "m", v: "v" },
  families: { E: ENERGY, m: MASS, v: SPEED },
  equation: "KE = ½ × m × v²",
  solvedFor: "E",
  rearranged: {
    E: "KE = ½ × m × v²",
    m: "m = 2 × KE ÷ v²",
    v: "v = √(2 × KE ÷ m)",
  },
  solve(unknown, { E, m, v }) {
    if (unknown !== "E" && E < 0) {
      return fail("energy-negative");
    }
    if (unknown !== "m" && !(m > 0)) {
      return fail("mass-not-positive");
    }
    if (unknown === "E") {
      return 0.5 * m * v * v;
    }
    if (unknown === "m") {
      return v === 0 ? fail("speed-zero") : (2 * E) / (v * v);
    }
    return Math.sqrt((2 * E) / m);
  },
};

export type RotationalKey = "E" | "I" | "w";

export const ROTATIONAL: RelationDef<RotationalKey> = {
  keys: ["E", "I", "w"],
  symbols: { E: "KE", I: "I", w: "ω" },
  families: { E: ENERGY, I: MOMENT_OF_INERTIA, w: ANGULAR_SPEED },
  equation: "KE = ½ × I × ω²",
  solvedFor: "E",
  rearranged: {
    E: "KE = ½ × I × ω²",
    I: "I = 2 × KE ÷ ω²",
    w: "ω = √(2 × KE ÷ I)",
  },
  solve(unknown, { E, I, w }) {
    if (unknown !== "E" && E < 0) {
      return fail("energy-negative");
    }
    if (unknown !== "I" && !(I > 0)) {
      return fail("inertia-not-positive");
    }
    if (unknown === "E") {
      return 0.5 * I * w * w;
    }
    if (unknown === "I") {
      return w === 0 ? fail("angular-speed-zero") : (2 * E) / (w * w);
    }
    return Math.sqrt((2 * E) / I);
  },
};

/** Speed multiples shown in the "what if it were faster" table. */
export const SPEED_MULTIPLES = [0.5, 2, 3] as const;

/** Kinetic energy scales with the square of the speed. */
export function energyAtSpeedMultiple(
  energy: number,
  multiple: number
): number {
  return energy * multiple * multiple;
}

/**
 * Fraction of the speed of light below which ½mv² is within 1% of the relativistic
 * (γ − 1)mc²: solving (γ − 1) / (½β²) = 1.01 gives β ≈ 0.1148.
 */
export const CLASSICAL_SPEED_FRACTION = 0.1148;
const SPEED_OF_LIGHT = 299_792_458;

/** True when v is fast enough that the non-relativistic formula is off by over 1%. */
export function isRelativistic(speed: number): boolean {
  return Math.abs(speed) / SPEED_OF_LIGHT > CLASSICAL_SPEED_FRACTION;
}
